/** Backend timestamps are UTC (RFC3339 Z or `YYYY-MM-DD[ HH:MM[:SS]]`). Display in UTC or browser local. */

import type { TimeZoneMode } from "@/stores/timezone";

const pad = (n: number) => String(n).padStart(2, "0");

/** Parse backend/CLI wall times as UTC. */
export function parseUtcInstant(d: string | number | Date): Date | null {
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  if (typeof d === "number") {
    const t = new Date(d);
    return isNaN(t.getTime()) ? null : t;
  }
  const s = String(d).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const t = new Date(`${s}T00:00:00Z`);
    return isNaN(t.getTime()) ? null : t;
  }
  const wall = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (wall) {
    const t = new Date(`${wall[1]}T${wall[2]}:${wall[3]}:${wall[4] || "00"}Z`);
    return isNaN(t.getTime()) ? null : t;
  }
  const t = new Date(s);
  return isNaN(t.getTime()) ? null : t;
}

type DateParts = { y: number; mo: number; d: number; h: number; mi: number; s: number };

function parts(t: Date, mode: TimeZoneMode): DateParts {
  if (mode === "local") {
    return {
      y: t.getFullYear(),
      mo: t.getMonth() + 1,
      d: t.getDate(),
      h: t.getHours(),
      mi: t.getMinutes(),
      s: t.getSeconds(),
    };
  }
  return {
    y: t.getUTCFullYear(),
    mo: t.getUTCMonth() + 1,
    d: t.getUTCDate(),
    h: t.getUTCHours(),
    mi: t.getUTCMinutes(),
    s: t.getUTCSeconds(),
  };
}

function localeOpts(mode: TimeZoneMode): Intl.DateTimeFormatOptions {
  return mode === "utc" ? { timeZone: "UTC" } : {};
}

/** Format as "YYYY-MM-DD HH:mm". */
export function formatDateTime(d: string | number | Date, mode: TimeZoneMode = "utc"): string {
  const t = parseUtcInstant(d);
  if (!t) return "";
  const p = parts(t, mode);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)} ${pad(p.h)}:${pad(p.mi)}`;
}

/** Format as "YYYY-MM-DD". */
export function formatDate(d: string | number | Date, mode: TimeZoneMode = "utc"): string {
  const t = parseUtcInstant(d);
  if (!t) return "";
  const p = parts(t, mode);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
}

/** Compact "Jan 15, 14:30" for revision / list labels. */
export function formatDateTimeShort(d: string | number | Date, mode: TimeZoneMode = "utc"): string {
  const t = parseUtcInstant(d);
  if (!t) return "";
  return t.toLocaleString(undefined, {
    ...localeOpts(mode),
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Chart axis: "Jan 15, 24". */
export function formatChartDate(d: string | number | Date, mode: TimeZoneMode = "utc"): string {
  const t = parseUtcInstant(d);
  if (!t) return String(d).slice(0, 10);
  return t.toLocaleDateString(undefined, {
    ...localeOpts(mode),
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

/** Live clock "YYYY-MM-DD HH:mm:ss". */
export function formatClock(d: Date, mode: TimeZoneMode): string {
  const p = parts(d, mode);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)} ${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`;
}

/** Format as "YYYY-MM-DD HH:mm" (UTC). Prefer `formatDateTime` with mode. */
export function formatUtcDateTime(d: string | number | Date): string {
  return formatDateTime(d, "utc");
}
